/**
 * API client and React Query hooks for the True Odds service.
 */
import axios from 'axios';

// Backend URL. When served from the same origin (production), use empty string.
// For local dev with separate frontend/backend processes, set REACT_APP_API_URL=http://localhost:8001
const API_BASE = process.env.REACT_APP_API_URL || '';

// Create axios instance with defaults
export const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach Google ID token to every request
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('google_id_token');
  if (token && token !== 'disabled') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401: re-auth. Do not reload on 403 (e.g. batch suspensions on a non–api_only writer).
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('google_id_token');
      localStorage.removeItem('google_user');
      window.location.reload();
    }
    return Promise.reject(error);
  },
);

// =============================================================================
// Health & Status
// =============================================================================

export const fetchHealth = async () => {
  const { data } = await apiClient.get('/health');
  return data;
};

export const fetchStatus = async () => {
  const { data } = await apiClient.get('/api/status');
  return data;
};

export const fetchSystemStatus = async () => {
  const { data } = await apiClient.get('/api/system-status');
  return data;
};

// =============================================================================
// Quotes
// =============================================================================

export const fetchQuote = async (marketKey) => {
  const { data } = await apiClient.get(`/api/quote/${encodeURIComponent(marketKey)}`);
  return data;
};

/**
 * Fetch quotes with server-side filtering and pagination.
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Max results (default 100, max 500)
 * @param {number} params.offset - Skip first N results
 * @param {string} params.sort_by - Sort order: last_sse_update, timestamp, p_fair, n_books
 * @param {boolean} params.exclude_live - Exclude live fixtures (default true)
 * @param {string} params.league - Filter by league (partial match)
 * @param {string} params.market_id - Filter by market_id (exact match)
 * @param {string} params.player_name - Filter by player name (partial match)
 * @param {string} params.team_name - Filter by team name (partial match)
 * @param {boolean} params.gate_allowed - Filter by gate status
 * @param {number} params.min_books - Filter by minimum books
 */
export const fetchAllQuotes = async (params = {}) => {
  const { data } = await apiClient.get('/api/quotes', { params });
  return data;
};

/**
 * All quotes for one fixture (every player / market on that game).
 * @param {boolean} [options.includeAlts] - Backfill `alt_overs` from stored ladders
 *   (Game Summary ladders only; adds a ladder query over every market on the fixture).
 */
export const fetchQuotesByFixture = async (fixtureId, { includeAlts = false } = {}) => {
  const { data } = await apiClient.get(
    `/api/quotes/fixture/${encodeURIComponent(fixtureId)}`,
    includeAlts ? { params: { include_alts: true } } : undefined
  );
  return data;
};

/** Batched per-line entry stats for every market on a fixture (Game Summary). */
export const fetchFixtureEntryStatsByLine = async (fixtureId) => {
  const { data } = await apiClient.get(
    `/api/quotes/fixture/${encodeURIComponent(fixtureId)}/entry-stats-by-line`,
    { params: { _t: Date.now() } }
  );
  return data;
};

/**
 * Fetch summary of active quotes (counts, filter options) without loading full data.
 */
export const fetchQuotesSummary = async (params = {}) => {
  const { data } = await apiClient.get('/api/quotes/summary', { params });
  return data;
};

/**
 * Fetch markets with highest innovation (price movement) in the last N hours.
 * @param {Object} params
 * @param {number} params.limit - Max results (default 100)
 * @param {number} params.hours - Lookback window (default 1.0)
 * @param {boolean} params.exclude_live - Exclude live fixtures (default true)
 */
export const fetchQuoteMovers = async (params = {}) => {
  const { data } = await apiClient.get('/api/quotes/movers', { params });
  return data;
};

/**
 * Historical book prices + competitor panel.
 * ``line`` null/omitted = follow the quote: the server translates each sample onto the
 * baseline it was quoting at that timestamp. Pass a line only to pin the scrub to one rung,
 * which also restricts the raw traces to books actually posting it.
 */
export const fetchQuotePriceHistory = async (marketKey, params = {}) => {
  const { hours = 4, limit = 500, line = null, panelTs = null } = params;
  const query = { hours, max_points: limit, _t: Date.now() };
  if (line != null && Number.isFinite(Number(line))) query.line = Number(line);
  // The competitor panel renders one timestamp, so the server only builds panel_* for the
  // sample nearest this ts (latest when omitted). Scrubbing refetches with the new ts.
  if (panelTs != null && Number.isFinite(Number(panelTs))) query.panel_ts = Number(panelTs);
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/prices`,
    { params: query }
  );
  return data;
};

export const fetchQuoteDiagnosticsHistory = async (marketKey, params = {}) => {
  const { hours = 4, diagnostic_type = 'gating' } = params;
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/diagnostics`,
    { params: { hours, diagnostic_type, _t: Date.now() } }
  );
  return data;
};

export const fetchCompetitorSignals = async (marketKey, params = {}) => {
  const { hours = 6, limit = 50, sportsbook, event } = params;
  const query = { hours, limit, _t: Date.now() };
  if (sportsbook) query.sportsbook = sportsbook;
  if (event) query.event = event;
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/competitor-signals`,
    { params: query }
  );
  return data;
};

export const fetchEntriesDiagnosticsHistory = async (marketKey, params = {}) => {
  const { hours = 6, series = 'P_post' } = params;
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/entries-diagnostics`,
    { params: { hours, series, _t: Date.now() } }
  );
  return data;
};

export const fetchQuoteHistory = async (marketKey, params = {}) => {
  const { hours = 4 } = params;
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/history`,
    { params: { hours, _t: Date.now() } }
  );
  return data;
};

export const fetchQuoteLines = async (marketKey, params = {}) => {
  const { hours = 4 } = params;
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/lines`,
    { params: { hours, _t: Date.now() } }
  );
  return data;
};

export const fetchQuoteLadder = async (marketKey, params = {}) => {
  const { hours = 4 } = params;
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/ladder`,
    { params: { hours, _t: Date.now() } }
  );
  return data;
};

/** Per leg line × true P(over) integer % (from entry probability): fees + liability (Risk).
 *  Also includes straight/parlay liability splits and ``leg_dist`` for the Liability tab.
 */
export const fetchEntryStatsByLine = async (marketKey) => {
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/entry-stats-by-line`,
    { params: { _t: Date.now() } }
  );
  return data;
};

export const fetchQuoteUpdates = async (marketKey, params = {}) => {
  const { hours = 4 } = params;
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/updates`,
    { params: { hours, _t: Date.now() } }
  );
  return data;
};

/** Internal discrete OB: P_to_OB snapshot + remaining depth per level. */
export const fetchOrderBook = async (marketKey) => {
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/order-book`,
    { params: { _t: Date.now() } }
  );
  return data;
};

/** Operator clear: zero liquidity at that rung only (no top-first sweep); OB_to_P → EntriesKF. */
export const postOrderBookClearLevel = async (marketKey, body) => {
  const payload = { ...body };
  if (payload.acted_by != null && !String(payload.acted_by).trim()) delete payload.acted_by;
  const { data } = await apiClient.post(
    `/api/quotes/${encodeURIComponent(marketKey)}/order-book/clear-level`,
    payload
  );
  return data;
};

/** Operator: refill one rung by stripping top-first depth from the opposite internal ladder (no EntriesKF). */
export const postOrderBookAddLevel = async (marketKey, body) => {
  const { data } = await apiClient.post(
    `/api/quotes/${encodeURIComponent(marketKey)}/order-book/add-level`,
    body
  );
  return data;
};

/** OB builder preview: what a proposed shape would produce. Read-only — safe to call while typing. */
export const postOrderBookBuilderPreview = async (marketKey, overrides) => {
  const { data } = await apiClient.post(
    `/api/quotes/${encodeURIComponent(marketKey)}/order-book/builder/preview`,
    { overrides }
  );
  return data;
};

/** OB builder commit: optional (x, P) reseed, store the shape, rebuild. The only mutating call. */
export const postOrderBookBuilderApply = async (marketKey, body) => {
  const payload = { ...body };
  if (payload.acted_by != null && !String(payload.acted_by).trim()) delete payload.acted_by;
  const { data } = await apiClient.post(
    `/api/quotes/${encodeURIComponent(marketKey)}/order-book/builder/apply`,
    payload
  );
  return data;
};

/** Leave builder mode: drop the stored shape, rebuild from the model. */
export const deleteOrderBookBuilder = async (marketKey, actedBy) => {
  const { data } = await apiClient.delete(
    `/api/quotes/${encodeURIComponent(marketKey)}/order-book/builder`,
    { params: actedBy ? { acted_by: actedBy } : {} }
  );
  return data;
};

/** Append one deeper synthetic rung on one side only (no opposite transfer; no EntriesKF). */
export const postOrderBookAppendBottom = async (marketKey, body) => {
  const { data } = await apiClient.post(
    `/api/quotes/${encodeURIComponent(marketKey)}/order-book/append-bottom`,
    body
  );
  return data;
};

/** Operator: full P_to_OB re-seed (allow_new_levels) — server-side liquidity / depth refresh. */
export const postOrderBookRefreshDepth = async (marketKey, opts = {}) => {
  const {
    mode = 'two_sided',
    acted_by: actedBy,
    override_soft_gates: overrideSoft,
    persist_force_two_sided: persistForceTwoSided,
    persist_force_one_sided: persistForceOneSided,
  } = opts;
  const body = {
    mode,
    override_soft_gates: Boolean(overrideSoft),
    persist_force_two_sided: Boolean(persistForceTwoSided),
    persist_force_one_sided: Boolean(persistForceOneSided),
  };
  if (actedBy != null && String(actedBy).trim()) body.acted_by = String(actedBy).trim();
  const { data } = await apiClient.post(
    `/api/quotes/${encodeURIComponent(marketKey)}/order-book/refresh-depth`,
    body
  );
  return data;
};

/** Audit log of manual / entry OB clears for a market. */
export const fetchOrderBookClearLog = async (marketKey, limit = 100) => {
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/order-book/clear-log`,
    { params: { limit, _t: Date.now() } }
  );
  return data;
};

/** Activity log: base KF + EntriesKF diagnostics + OB clears (merged timeline). */
export const fetchMasterActivityLog = async (marketKey, params = {}) => {
  const { hours = 168, limit = 400 } = params;
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/master-log`,
    { params: { hours, limit, _t: Date.now() } }
  );
  return data;
};

/**
 * Fetch user entries for a market (for flow / manual intervention).
 * @param {string} marketKey
 * @param {object} params - { limit?, min_sharp?, min_fee? }
 */
export const fetchMarketEntries = async (marketKey, params = {}) => {
  const { data } = await apiClient.get(
    `/api/quotes/${encodeURIComponent(marketKey)}/entries`,
    { params: { ...params, _t: Date.now() } }
  );
  return data;
};

/**
 * Top markets by total fees in the last N minutes (overview "Recently entered").
 * @param {object} params - { minutes?, limit? } - minutes default from server (e.g. 10)
 */
export const fetchRecentlyEnteredMarkets = async (params = {}) => {
  const { data } = await apiClient.get('/api/entries/recently-entered', {
    params: { ...params, _t: Date.now() },
  });
  return data;
};

/**
 * Raw entries from the last N minutes (Entries view). Sort and filter by all dimensions.
 * @param {object} params - minutes (<=0 uses server 7-day lookback on created_ts), limit, sort_by, sort_order, market_key (exact),
 *   player, market (substring filters), user_id, outcome, sport, provider,
 *   fixture_start_after, fixture_start_before (Unix UTC seconds), wager_type,
 *   min_fee, min_sharp
 */
export const fetchRecentEntries = async (params = {}) => {
  const { data } = await apiClient.get('/api/entries/recent', {
    params: { ...params, _t: Date.now() },
  });
  return data;
};

/** Graded Pub/Sub snapshots, either every event or the latest state per parlay. */
export const fetchGradedSlips = async (params = {}) => {
  const { data } = await apiClient.get('/api/entries/graded', {
    params: { ...params, _t: Date.now() },
  });
  return data;
};

/**
 * Aggregated exposure: fees, liability, avg sharp — one row per market × player × fixture × line
 * (outcome_value). Same filters as fetchRecentEntries plus sort_by.
 */
export const fetchEntriesExposure = async (params = {}) => {
  const { data } = await apiClient.get('/api/entries/exposure', {
    params: { ...params, _t: Date.now() },
  });
  return data;
};

/**
 * Hot replicated parlay compositions (full-leg fingerprint counts).
 * @param {object} params - min_n_reg, min_n_hf, limit
 */
export const fetchHotCompositions = async (params = {}) => {
  const { data } = await apiClient.get('/api/entries/hot-compositions', {
    params: { ...params, _t: Date.now() },
  });
  return data;
};

/**
 * Active quoted markets with entry fee + liability rollups in-window (Markets tab).
 * @param {object} params - minutes, limit, exclude_complete, player, market, league, min_fees_window,
 *   market_id_exact, sort_by (fees_window|main_line_liability|fixture|market|market_player_fixture), sort_order
 */
export const fetchQuotedMarketsActivity = async (params = {}) => {
  const { data } = await apiClient.get('/api/quotes/markets-activity', {
    params: { ...params, _t: Date.now() },
  });
  return data;
};

// =============================================================================
// Fixtures
// =============================================================================

export const fetchDoneFixtures = async () => {
  const { data } = await apiClient.get('/api/done-fixtures');
  return data;
};

export const fetchScheduledFixtures = async () => {
  const { data } = await apiClient.get('/api/fixtures/schedule');
  return data;
};

/** Active fixtures with engine counts (trading dash game rail). */
export const fetchActiveFixturesSummary = async () => {
  const { data } = await apiClient.get('/api/fixtures/active/summary');
  return data;
};

// =============================================================================
// Markets
// =============================================================================

export const fetchMarkets = async (fixtureId = null) => {
  const params = fixtureId ? { fixture: fixtureId } : {};
  const { data } = await apiClient.get('/api/markets', { params });
  return data;
};

export const fetchMarket = async (marketKey) => {
  const { data } = await apiClient.get(`/api/markets/${encodeURIComponent(marketKey)}`);
  return data;
};

/** Derived-model targets: Postgres snapshots + liability window + market_quotes join. Params: minutes, sort_by, sort_order, fuse_ok_only, fees_positive_only, league, sport, limit (default 100). */
export const fetchDerivedMarketsActive = async (params = {}) => {
  const { data } = await apiClient.get('/api/markets/derived-active', {
    params: { minutes: 30, sort_by: 'liability', sort_order: 'desc', limit: 100, ...params, _t: Date.now() },
  });
  return data;
};

/** Operations -> Derived manual inputs: one row per leg a trader may fill, with its book state. */
export const fetchDerivedManualInputs = async (params = {}) => {
  const { data } = await apiClient.get('/api/markets/derived-manual-inputs', {
    params: { include_filled: true, ...params, _t: Date.now() },
  });
  return data;
};

/** Dashboard Models tab: YAML rules, parent fuse diagnostics (200 with ``derived_model_available: false`` when none). */
export const fetchDerivedModelDetail = async (marketKey) => {
  const { data } = await apiClient.get(
    `/api/markets/${encodeURIComponent(marketKey)}/derived-model`,
    { params: { _t: Date.now() } }
  );
  return data;
};

// =============================================================================
// Filters
// =============================================================================

export const fetchFilters = async () => {
  const { data } = await apiClient.get('/api/filters');
  return data;
};

export const fetchFilterLeagues = async () => {
  const { data } = await apiClient.get('/api/filters/leagues');
  return data;
};

export const fetchFilterMarketIds = async () => {
  const { data } = await apiClient.get('/api/filters/market-ids');
  return data;
};

export const fetchFilterTeams = async () => {
  const { data } = await apiClient.get('/api/filters/teams');
  return data;
};

export const fetchFilterPlayers = async () => {
  const { data } = await apiClient.get('/api/filters/players');
  return data;
};

// =============================================================================
// Manual Gating (Overrides)
// =============================================================================

/**
 * Gate market (block), or shape quote and leave open.
 * @param {string} marketKey
 * @param {object} opts - { reason?, gatedBy?, price?, line?, price_under?, force_two_sided?, force_one_sided?, manual_logit_margin_scale?, manual_prob_margin_over?, manual_prob_margin_under?, leave_open?, manual_alts_allow?, manual_alt_margin_floor?, manual_alt_margin_ceiling? } — use manual_alts_allow: null (with floor/ceiling null) to clear manual alt policy.
 */
export const gateMarket = async (marketKey, opts = {}) => {
  const {
    reason,
    gatedBy = 'manual',
    price,
    line,
    price_under,
    force_two_sided,
    force_one_sided,
    manual_logit_margin_scale,
    manual_prob_margin_over,
    manual_prob_margin_under,
    leave_open,
    manual_alts_allow,
    manual_alt_margin_floor,
    manual_alt_margin_ceiling,
  } = opts;
  const body = { gated_by: gatedBy };
  if (reason != null && String(reason).trim()) body.reason = String(reason).trim();
  if (price != null && price > 0) body.price = Number(price);
  if (line != null && !isNaN(parseFloat(line))) body.line = parseFloat(line);
  if (price_under != null && price_under > 0) body.price_under = Number(price_under);
  if (force_two_sided === true) body.force_two_sided = true;
  if (force_one_sided === true) body.force_one_sided = true;
  if (Object.prototype.hasOwnProperty.call(opts, 'manual_logit_margin_scale')) {
    const ml = manual_logit_margin_scale;
    body.manual_logit_margin_scale =
      ml == null || ml === '' || (typeof ml === 'number' && Number.isNaN(ml))
        ? null
        : Number(ml);
  }
  if (Object.prototype.hasOwnProperty.call(opts, 'manual_prob_margin_over')) {
    const v = manual_prob_margin_over;
    body.manual_prob_margin_over =
      v == null || v === '' || (typeof v === 'number' && Number.isNaN(v)) ? null : Number(v);
  }
  if (Object.prototype.hasOwnProperty.call(opts, 'manual_prob_margin_under')) {
    const v = manual_prob_margin_under;
    body.manual_prob_margin_under =
      v == null || v === '' || (typeof v === 'number' && Number.isNaN(v)) ? null : Number(v);
  }
  if (leave_open === true) body.leave_open = true;
  // Omit when undefined. Explicit null clears manual_alts_allow in DB (engine default).
  if (Object.prototype.hasOwnProperty.call(opts, 'manual_alts_allow')) {
    body.manual_alts_allow =
      manual_alts_allow === null || manual_alts_allow === ''
        ? null
        : Boolean(manual_alts_allow);
  }
  if (manual_alt_margin_floor !== undefined) {
    body.manual_alt_margin_floor =
      manual_alt_margin_floor == null || manual_alt_margin_floor === ''
        ? null
        : Number(manual_alt_margin_floor);
  }
  if (manual_alt_margin_ceiling !== undefined) {
    body.manual_alt_margin_ceiling =
      manual_alt_margin_ceiling == null || manual_alt_margin_ceiling === ''
        ? null
        : Number(manual_alt_margin_ceiling);
  }
  const { data } = await apiClient.post(
    `/api/markets/${encodeURIComponent(marketKey)}/gate`,
    body
  );
  return data;
};

export const ungateMarket = async (marketKey) => {
  const { data } = await apiClient.delete(
    `/api/markets/${encodeURIComponent(marketKey)}/gate`
  );
  return data;
};

/**
 * Bulk gate / ungate / 1w/2w shaping for trading dash (fans out on api_only when configured).
 * @param {object} body - { market_keys, ungate?, reason?, gated_by?, force_one_sided?, force_two_sided?, leave_open?, fixture_id?, fanout_writers?, manual_prob_margin_over?, manual_prob_margin_under? }
 */
export const bulkGateMarkets = async (body) => {
  // Many markets + OB refresh + fixture republish can exceed the default axios timeout.
  const n = Array.isArray(body?.market_keys) ? body.market_keys.length : 1;
  const timeout = Math.min(120_000, Math.max(30_000, 8_000 + 2_000 * n));
  const { data } = await apiClient.post('/api/markets/bulk-gate', body, { timeout });
  return data;
};

/** Lock KF baseline to a line (disables automatic baseline selection / shift until cleared). */
export const setManualBaselineLock = async (marketKey, opts = {}) => {
  const { baseLine, setBy = 'manual' } = opts;
  const body = { base_line: Number(baseLine), set_by: setBy };
  const { data } = await apiClient.post(
    `/api/markets/${encodeURIComponent(marketKey)}/manual-base-line`,
    body
  );
  return data;
};

export const clearManualBaselineLock = async (marketKey) => {
  const { data } = await apiClient.delete(
    `/api/markets/${encodeURIComponent(marketKey)}/manual-base-line`
  );
  return data;
};

export const fetchMarketOverride = async (marketKey) => {
  const { data } = await apiClient.get(
    `/api/markets/${encodeURIComponent(marketKey)}/override`
  );
  return data;
};

/**
 * Per-market: ignore base-KF → EntriesKF competitor sync and z²-gated synthetic OB ladder refresh.
 * @param {string} marketKey
 * @param {{ enabled: boolean }} opts
 */
export const setMuteCompetitorSignals = async (marketKey, opts = {}) => {
  const enabled = opts.enabled !== false;
  const { data } = await apiClient.post(
    `/api/markets/${encodeURIComponent(marketKey)}/mute-competitor`,
    { enabled }
  );
  return data;
};

/**
 * Raise EntriesKF posterior variance: P += delta_p (untagged / no jump decay; fair x unchanged).
 * @param {string} marketKey
 * @param {{ deltaP: number, actedBy?: string }} opts
 */
export const injectMarketUncertainty = async (marketKey, opts = {}) => {
  const { deltaP, actedBy } = opts;
  const body = { delta_p: Number(deltaP) };
  if (actedBy != null && String(actedBy).trim()) body.acted_by = String(actedBy).trim();
  const { data } = await apiClient.post(
    `/api/markets/${encodeURIComponent(marketKey)}/inject-uncertainty`,
    body
  );
  return data;
};

/**
 * Hard-reseed baseline line + posterior (x or p, P).
 * @param {string} marketKey
 * @param {{ line: number, P: number, x?: number, p?: number, lockBaseline?: boolean, actedBy?: string }} opts
 */
export const reseedMarketPosterior = async (marketKey, opts = {}) => {
  const { line, P, x, p, lockBaseline = true, actedBy } = opts;
  const body = {
    line: Number(line),
    P: Number(P),
    lock_baseline: lockBaseline !== false,
  };
  if (x != null && Number.isFinite(Number(x))) body.x = Number(x);
  else if (p != null && Number.isFinite(Number(p))) body.p = Number(p);
  if (actedBy != null && String(actedBy).trim()) body.acted_by = String(actedBy).trim();
  const { data } = await apiClient.post(
    `/api/markets/${encodeURIComponent(marketKey)}/reseed-posterior`,
    body
  );
  return data;
};

export const fetchBookCompetitorMutes = async (marketKey) => {
  const { data } = await apiClient.get(
    `/api/markets/${encodeURIComponent(marketKey)}/book-competitor-mutes`
  );
  return data;
};

/** Per-(market, sportsbook): skip competitor signals for one book (prices still stored). */
export const setMuteBookCompetitor = async (marketKey, opts = {}) => {
  const { sportsbook, enabled = true, reason, muted_by: mutedBy } = opts;
  const { data } = await apiClient.post(
    `/api/markets/${encodeURIComponent(marketKey)}/mute-book-competitor`,
    {
      sportsbook,
      enabled,
      ...(reason != null ? { reason } : {}),
      ...(mutedBy != null ? { muted_by: mutedBy } : {}),
    }
  );
  return data;
};

export const fetchGatedMarkets = async () => {
  const { data } = await apiClient.get('/api/markets/gated');
  return data;
};

/** Markets with any manual dashboard intervention (block, prices, force sided, logit scale). */
export const fetchManualInterventions = async () => {
  const { data } = await apiClient.get('/api/markets/manual-interventions');
  return data;
};

/** Recent internal OB ladder clears (``order_book_clear_log``), sport-scoped like manual interventions. */
export const fetchObOrderBookClearEvents = async (params = {}) => {
  const { limit = 100, hours, source, qualifyingOnly = true } = params;
  const qp = { limit, qualifying_only: qualifyingOnly };
  if (hours != null && Number.isFinite(Number(hours))) qp.hours = Number(hours);
  if (source != null && String(source).trim()) qp.source = String(source).trim();
  const { data } = await apiClient.get('/api/markets/order-book-clear-events', {
    params: qp,
  });
  return data;
};

/**
 * Fixture payload suspensions (api_only). Matching markets are SUSPENDED in the published payload only.
 * @param {Object} params - { activeOnly?: boolean, limit?: number, sportBucket?: string }
 */
export const fetchBatchPublishSuspensions = async (params = {}) => {
  const { activeOnly = false, limit = 200, sportBucket } = params;
  const qp = { active_only: activeOnly, limit };
  if (sportBucket) qp.sport_bucket = sportBucket;
  const { data } = await apiClient.get('/api/markets/batch-publish-suspensions', {
    params: qp,
  });
  return data;
};

/**
 * @param {Object} body - sport_bucket, scope, league?, wager_type?, reason, created_by?, confirm_all_markets?, fanout_writers?
 * scope=all requires confirm_all_markets: true
 */
export const createBatchPublishSuspension = async (body) => {
  const { data } = await apiClient.post('/api/markets/batch-publish-suspensions', body);
  return data;
};

/** Remove (end) a batch suspension. Server also accepts POST …/end for compatibility. */
export const removeBatchPublishSuspension = async (suspensionId) => {
  const { data } = await apiClient.delete(
    `/api/markets/batch-publish-suspensions/${encodeURIComponent(suspensionId)}`
  );
  return data;
};

/**
 * Batch competitor mute rules (api_only). Writers OR these with per-row mute_competitor_signals.
 * @param {Object} params - { activeOnly?: boolean, limit?: number, sportBucket?: string }
 */
export const fetchBatchCompetitorMutes = async (params = {}) => {
  const { activeOnly = false, limit = 200, sportBucket } = params;
  const qp = { active_only: activeOnly, limit };
  if (sportBucket) qp.sport_bucket = sportBucket;
  const { data } = await apiClient.get('/api/markets/batch-competitor-mutes', {
    params: qp,
  });
  return data;
};

/**
 * @param {Object} body - sport_bucket, scope, league?, wager_type?, reason, created_by?, confirm_all_markets?, fanout_writers?
 */
export const createBatchCompetitorMute = async (body) => {
  const { data } = await apiClient.post('/api/markets/batch-competitor-mutes', body);
  return data;
};

export const removeBatchCompetitorMute = async (ruleId) => {
  const { data } = await apiClient.delete(
    `/api/markets/batch-competitor-mutes/${encodeURIComponent(ruleId)}`
  );
  return data;
};

// =============================================================================
// Manual lineups (fixture roster + commit)
// =============================================================================

/** @param {{ sport?: string, league?: string }} [params] - optional server-side filters (UI uses client-side for facet lists) */
export const fetchManualLineupEligibleFixtures = async (params = {}) => {
  const { data } = await apiClient.get('/api/manual-lineups/eligible-fixtures', { params });
  return data;
};

export const fetchManualLineupRoster = async (fixtureId) => {
  const { data } = await apiClient.get(
    `/api/manual-lineups/fixtures/${encodeURIComponent(fixtureId)}/roster`
  );
  return data;
};

/** @param {Object} body - { league?, url?, min_match_score? } */
export const previewRotowireSiteLineup = async (fixtureId, body = {}) => {
  const { data } = await apiClient.post(
    `/api/manual-lineups/fixtures/${encodeURIComponent(fixtureId)}/rotowire-site-preview`,
    body
  );
  return data;
};

/** @param {Object} body - { players: [{ sleeper_player_id, side, selected }], source?, committed? } */
export const commitManualLineup = async (fixtureId, body) => {
  const { data } = await apiClient.post(
    `/api/manual-lineups/fixtures/${encodeURIComponent(fixtureId)}`,
    body
  );
  return data;
};

// =============================================================================
// Opta live (fixtures with is_live_covered; shared DB with live writer)
// =============================================================================

/** Live-covered fixtures in retention window (default 3 days). */
export const fetchLiveKillSwitch = async () => {
  const { data } = await apiClient.get('/api/controls/live-kill-switch', {
    params: { _t: Date.now() },
  });
  return data;
};

export const setLiveKillSwitch = async (body) => {
  const { data } = await apiClient.post('/api/controls/live-kill-switch', body);
  return data;
};

export const setFixtureLifecycleStatus = async (gameId, body) => {
  const { data } = await apiClient.post(
    `/api/fixtures/${encodeURIComponent(gameId)}/status`,
    body
  );
  return data;
};

export const clearFixtureStatusOverride = async (gameId, fanoutWriters = true) => {
  const { data } = await apiClient.delete(
    `/api/fixtures/${encodeURIComponent(gameId)}/status-override`,
    { params: { fanout_writers: fanoutWriters } }
  );
  return data;
};

export const fetchOptaLiveFixtures = async (params = {}) => {
  const {
    retention_days: retentionDays,
    lookahead_days: lookaheadDays,
    live_covered_only: liveCoveredOnly,
  } = params;
  const query = { _t: Date.now() };
  if (retentionDays != null) query.retention_days = retentionDays;
  if (lookaheadDays != null) query.lookahead_days = lookaheadDays;
  if (liveCoveredOnly != null) query.live_covered_only = liveCoveredOnly;
  const { data } = await apiClient.get('/api/opta/fixtures/live', { params: query });
  return data;
};

/** Events, block state, and quoted markets for one fixture (optic game_id). */
export const fetchOptaLiveDashboard = async (fixtureId, params = {}) => {
  const { opta_id: optaId, event_limit: eventLimit = 2500 } = params;
  const { data } = await apiClient.get(
    `/api/opta/fixtures/${encodeURIComponent(fixtureId)}/live-dashboard`,
    {
      params: {
        ...(optaId ? { opta_id: optaId } : {}),
        event_limit: eventLimit,
        _t: Date.now(),
      },
    }
  );
  return data;
};

/** Full persisted live log bundle for download (all PG live tables for one fixture). */
export const fetchOptaLiveExport = async (fixtureId, params = {}) => {
  const {
    opta_id: optaId,
    event_limit: eventLimit = 50000,
    movement_limit: movementLimit = 10000,
  } = params;
  const { data } = await apiClient.get(
    `/api/opta/fixtures/${encodeURIComponent(fixtureId)}/live-export`,
    {
      params: {
        ...(optaId ? { opta_id: optaId } : {}),
        event_limit: eventLimit,
        movement_limit: movementLimit,
        _t: Date.now(),
      },
    }
  );
  return data;
};

// =============================================================================
// Config
// =============================================================================

export const fetchConfig = async () => {
  const { data } = await apiClient.get('/api/config');
  return data;
};

export const fetchConfigOverrideTunables = async (profile = 'soccer') => {
  const { data } = await apiClient.get('/api/config/overrides/tunables', {
    params: { profile },
  });
  return data;
};

export const fetchConfigOverridesSummary = async () => {
  const { data } = await apiClient.get('/api/config/overrides');
  return data;
};

export const fetchConfigOverridesView = async (profile, marketId) => {
  const { data } = await apiClient.get('/api/config/overrides/view', {
    params: { profile, market_id: marketId || undefined },
  });
  return data;
};

export const fetchConfigOverridePreview = async ({
  profile,
  path,
  newValue,
  oldValue,
  marketId,
}) => {
  const { data } = await apiClient.get('/api/config/overrides/preview', {
    params: {
      profile,
      path,
      new_value: newValue,
      old_value: oldValue ?? undefined,
      market_id: marketId || undefined,
    },
  });
  return data;
};

export const setConfigOverridePath = async (payload) => {
  const { data } = await apiClient.patch('/api/config/overrides/path', payload);
  return data;
};

export const revertConfigOverrides = async (payload) => {
  const { data } = await apiClient.delete('/api/config/overrides', { data: payload });
  return data;
};

// =============================================================================
// Limits: config_limits view (ground truth) + limit_modifiers overrides + sync
// =============================================================================

/** Ground-truth limits from the config_limits view. Params: sport, limit_type, scope, user_tier, line_game_status, market, limit. */
export const fetchConfigLimits = async (params = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  const { data } = await apiClient.get('/api/limits/config', { params: clean });
  return data;
};

/** Base limits (config_limits_raw) + any active base override, for the base-grain editor. */
export const fetchBaseLimits = async (params = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  const { data } = await apiClient.get('/api/limits/base', { params: clean });
  return data;
};

/** Distinct values for the viewer search dropdowns. */
export const fetchLimitFilterOptions = async () => {
  const { data } = await apiClient.get('/api/limits/filter-options');
  return data;
};

/** All override rows in limit_modifiers (newest first). */
export const fetchLimitModifiers = async () => {
  const { data } = await apiClient.get('/api/limits/modifiers');
  return data;
};

/** Add one override. Body = targeting fields (blank = wildcard) + override_value_usd, expiry_ts, note. */
export const createLimitModifier = async (body) => {
  const { data } = await apiClient.post('/api/limits/modifiers', body);
  return data;
};

/** Remove one override by modifier_id. */
export const removeLimitModifier = async (modifierId) => {
  const { data } = await apiClient.delete(
    `/api/limits/modifiers/${encodeURIComponent(modifierId)}`
  );
  return data;
};

/** Fold current limits (overrides included) into the BQ limits_changelog history. */
export const syncLimitsToBQ = async () => {
  const { data } = await apiClient.post('/api/limits/sync-bq', {});
  return data;
};

/** Current seed multipliers + any active override, for the multiplier editor. */
export const fetchMultipliers = async (params = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  const { data } = await apiClient.get('/api/limits/multipliers', { params: clean });
  return data;
};

/** All multiplier-override rows (limit_multiplier_modifiers). */
export const fetchMultiplierModifiers = async () => {
  const { data } = await apiClient.get('/api/limits/multiplier-modifiers');
  return data;
};

/** Add one multiplier override. Body: limit_type, dimension, dimension_value, sport?, market?, user_tier?, multiplier, expiry_ts?, note?. */
export const createMultiplierModifier = async (body) => {
  const { data } = await apiClient.post('/api/limits/multiplier-modifiers', body);
  return data;
};

/** Remove one multiplier override by modifier_id. */
export const removeMultiplierModifier = async (modifierId) => {
  const { data } = await apiClient.delete(
    `/api/limits/multiplier-modifiers/${encodeURIComponent(modifierId)}`
  );
  return data;
};

// =============================================================================
// Model Market Hub
// =============================================================================

export const fetchModelMarketRules = async ({
  sport_bucket = 'wnba',
  sport = 'basketball',
  league = 'wnba',
} = {}) => {
  const { data } = await apiClient.get('/api/model-markets/rules', {
    params: { sport_bucket, sport, league },
  });
  return data;
};

export const fetchModelMarketRegistry = async ({
  sport_bucket = 'wnba',
  sport = 'basketball',
  league = 'wnba',
} = {}) => {
  const { data } = await apiClient.get('/api/model-markets/registry', {
    params: { sport_bucket, sport, league },
  });
  return data;
};

export const fetchModelMarketObservations = async ({
  model_name,
  fixture_id,
  market_id,
  sport_bucket = 'wnba',
  sport = 'basketball',
  league = 'wnba',
} = {}) => {
  const { data } = await apiClient.get('/api/model-markets/observations', {
    params: {
      model_name,
      fixture_id,
      ...(market_id ? { market_id } : {}),
      sport_bucket,
      sport,
      league,
    },
  });
  return data;
};

export const ensureModelMarketFixture = async (body) => {
  const { data } = await apiClient.post('/api/model-markets/ensure-fixture', body);
  return data;
};

export const ensureModelMarketPlayers = async (body) => {
  const { data } = await apiClient.post('/api/model-markets/ensure-players', body);
  return data;
};

export const putModelMarketFixtureSleeperLink = async (body) => {
  const { data } = await apiClient.put('/api/model-markets/fixture-sleeper-link', body);
  return data;
};

export const deleteModelMarketFixtureSleeperLink = async ({
  game_id,
  sport,
  league,
  sport_bucket,
  set_by,
} = {}) => {
  const { data } = await apiClient.delete('/api/model-markets/fixture-sleeper-link', {
    params: { game_id, sport, league, sport_bucket, set_by },
  });
  return data;
};

export const searchModelMarketPlayers = async ({
  q,
  sport,
  league,
  team,
  sport_bucket,
  limit = 50,
  include_feed_ids,
} = {}) => {
  const { data } = await apiClient.get('/api/model-markets/players/search', {
    params: { q, sport, league, team, sport_bucket, limit, include_feed_ids },
  });
  return data;
};

export const previewModelMarketPlayers = async (body) => {
  const { data } = await apiClient.post('/api/model-markets/players/preview', body);
  return data;
};

export const applyModelMarketPlayers = async (body) => {
  const { data } = await apiClient.post('/api/model-markets/players/apply', body);
  return data;
};

export const ensureModelMarkets = async (body) => {
  const { data } = await apiClient.post('/api/model-markets/ensure-markets', body);
  return data;
};

export const ingestModelMarketObservations = async (body) => {
  const { data } = await apiClient.post('/api/model-markets/ingest', body);
  return data;
};

/** Cross-fixture upload: every row carries its own fixture_id. Returns 200 with
 *  ok:false and a per-fixture `results` array when only some fixtures applied. */
export const ingestModelMarketObservationsBatch = async (body) => {
  const { data } = await apiClient.post('/api/model-markets/ingest-batch', body);
  return data;
};

export const fetchFixtureById = async (gameId) => {
  const { data } = await apiClient.get(`/api/fixtures/${encodeURIComponent(gameId)}`);
  return data;
};

// =============================================================================
// Risk Rank (Hot pill Featured legs)
// =============================================================================

export const fetchRiskRankConfig = async () => {
  const { data } = await apiClient.get('/api/risk-rank/config');
  return data;
};

export const fetchRiskRankBoard = async (league) => {
  const { data } = await apiClient.get(`/api/risk-rank/${encodeURIComponent(league)}`);
  return data;
};

export const fetchRiskRankCandidates = async (league, scopeType, scopeId) => {
  const { data } = await apiClient.get(`/api/risk-rank/${encodeURIComponent(league)}/candidates`, {
    params: { scope_type: scopeType, scope_id: scopeId },
  });
  return data;
};

/** body: scope_type, scope_id, market_key, and exactly one of pin_slot (1..top_n) / excluded: true */
export const setRiskRankOverride = async (league, body) => {
  const { data } = await apiClient.post(
    `/api/risk-rank/${encodeURIComponent(league)}/overrides`,
    body
  );
  return data;
};

export const clearRiskRankOverride = async (league, overrideId) => {
  const { data } = await apiClient.delete(
    `/api/risk-rank/${encodeURIComponent(league)}/overrides/${encodeURIComponent(overrideId)}`
  );
  return data;
};

export const recomputeRiskRank = async (league) => {
  const { data } = await apiClient.post(`/api/risk-rank/${encodeURIComponent(league)}/recompute`);
  return data;
};
